import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import customerService from '../../services/customerService';
import './CustomerDetailPage.css';

function CustomerDetailPage() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const [customerResponse, ordersResponse] = await Promise.all([
          customerService.getCustomer(id),
          customerService.getCustomerOrders(id),
        ]);
        setCustomer(customerResponse.data.data);
        setOrders(ordersResponse.data.data);
      } catch (err) {
        setError('Failed to load customer');
        console.error('Error fetching customer:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerData();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to change this customer's status to ${newStatus}?`)) {
      return;
    }

    setIsUpdating(true);
    try {
      const response = await customerService.updateCustomerStatus(id, newStatus);
      setCustomer(response.data.data);
      toast.success(`Customer status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update customer status');
      console.error('Error updating status:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'badge-success' },
      inactive: { label: 'Inactive', className: 'badge-secondary' },
      blocked: { label: 'Blocked', className: 'badge-danger' },
    };
    const config = statusConfig[status] || statusConfig.active;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'badge-warning' },
      processing: { label: 'Processing', className: 'badge-info' },
      shipped: { label: 'Shipped', className: 'badge-purple' },
      delivered: { label: 'Delivered', className: 'badge-success' },
      cancelled: { label: 'Cancelled', className: 'badge-danger' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="customer-detail-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading customer...</span>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="customer-detail-page">
        <div className="page-error">
          <h2>Customer Not Found</h2>
          <p>{error || 'The customer you are looking for does not exist.'}</p>
          <Link to="/customers" className="btn btn-primary">
            Back to Customers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-detail-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="breadcrumb">
            <Link to="/customers">Customers</Link>
            <span>/</span>
            <span>{customer.firstName} {customer.lastName}</span>
          </div>
          <div className="header-title-row">
            <div className="customer-avatar-large">
              {getInitials(customer.firstName, customer.lastName)}
            </div>
            <div className="customer-header-info">
              <h1>{customer.firstName} {customer.lastName}</h1>
              <p>{customer.email}</p>
            </div>
            {getStatusBadge(customer.status)}
          </div>
        </div>
        <div className="page-header-actions">
          {customer.status === 'active' && (
            <button
              className="btn btn-danger-outline"
              onClick={() => handleStatusChange('blocked')}
              disabled={isUpdating}
            >
              Block Customer
            </button>
          )}
          {customer.status === 'blocked' && (
            <button
              className="btn btn-primary"
              onClick={() => handleStatusChange('active')}
              disabled={isUpdating}
            >
              Unblock Customer
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders ({orders.length})
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="detail-grid">
          {/* Main Content */}
          <div className="detail-main">
            {/* Stats */}
            <div className="stats-row">
              <div className="stat-card">
                <span className="stat-value">{customer.totalOrders}</span>
                <span className="stat-label">Total Orders</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{formatPrice(customer.totalSpent)}</span>
                <span className="stat-label">Total Spent</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{formatPrice(customer.averageOrderValue)}</span>
                <span className="stat-label">Avg. Order Value</span>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="detail-section">
              <div className="section-header">
                <h3>Recent Orders</h3>
                <button className="btn btn-text" onClick={() => setActiveTab('orders')}>
                  View all
                </button>
              </div>
              {orders.length > 0 ? (
                <div className="orders-list">
                  {orders.slice(0, 3).map((order) => (
                    <Link key={order.id} to={`/orders/${order.id}`} className="order-item">
                      <div className="order-info">
                        <span className="order-number">{order.orderNumber}</span>
                        <span className="order-date">{formatDateTime(order.createdAt)}</span>
                      </div>
                      <div className="order-meta">
                        {getOrderStatusBadge(order.status)}
                        <span className="order-total">{formatPrice(order.total)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No orders yet</p>
                </div>
              )}
            </div>

            {/* Notes */}
            {customer.notes && (
              <div className="detail-section">
                <h3>Notes</h3>
                <p className="customer-notes">{customer.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="detail-sidebar">
            {/* Contact Info */}
            <div className="detail-section">
              <h3>Contact Information</h3>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <a href={`mailto:${customer.email}`} className="info-value link">
                    {customer.email}
                  </a>
                </div>
                {customer.phone && (
                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <span className="info-value">{customer.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="detail-section">
              <h3>Default Address</h3>
              <div className="address">
                <p>{customer.address.line1}</p>
                {customer.address.line2 && <p>{customer.address.line2}</p>}
                <p>{customer.address.city}</p>
                <p>{customer.address.postcode}</p>
                <p>{customer.address.country}</p>
              </div>
            </div>

            {/* Tags */}
            <div className="detail-section">
              <h3>Tags</h3>
              {customer.tags.length > 0 ? (
                <div className="tags-list">
                  {customer.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              ) : (
                <p className="no-tags">No tags assigned</p>
              )}
            </div>

            {/* Activity */}
            <div className="detail-section">
              <h3>Activity</h3>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Customer since</span>
                  <span className="info-value">{formatDate(customer.createdAt)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last order</span>
                  <span className="info-value">{formatDate(customer.lastOrderDate)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last updated</span>
                  <span className="info-value">{formatDate(customer.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="orders-tab">
          {orders.length > 0 ? (
            <div className="orders-table-container">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <Link to={`/orders/${order.id}`} className="order-link">
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td>{formatDateTime(order.createdAt)}</td>
                      <td>{getOrderStatusBadge(order.status)}</td>
                      <td>{order.items.length} items</td>
                      <td className="text-right">{formatPrice(order.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-orders">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              <h3>No orders yet</h3>
              <p>This customer hasn't placed any orders.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerDetailPage;
